import bbox from '@turf/bbox';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStores } from '../stores/useStores';

export function useMapState() {
  const { traplogStore } = useStores();
  const [viewport, setViewport] = useState({
    latitude: -40.9753799,
    longitude: 176.8903929,
    zoom: 4,
    height: 400,
    width: '100%'
  });
  const [initialBoundsSet, setInitialBoundsSet] = useState(false);
  const selectedInsectGroupId = traplogStore.selectedInsectGroupId;
  const mapRef = useRef();

  const boundaryData = useMemo(() => {
    const geojson = {
      type: 'FeatureCollection',
      features: []
    };
    traplogStore.trappingZones.forEach((trappingZone) => {
      const boundry = JSON.parse(trappingZone.boundry);
      if (boundry)
        geojson.features.push({
          type: 'Feature',
          geometry: boundry,
          properties: {
            status: traplogStore.trappingResults[trappingZone.id]
              ? traplogStore.trappingResults[trappingZone.id].overview.status
              : '',
            id: trappingZone.id,
            trappingZoneName: trappingZone.name
          }
        });
    });
    return geojson;
  }, [traplogStore.trappingZones, traplogStore.trappingResults]);

  const trapData = useMemo(() => {
    const traps = [];
    traplogStore.trappingZones.forEach((trappingZone) => {
      trappingZone.traps.forEach((trap) => {
        const location = trap.location;
        const isInGroup = trap.trap_lure.insects.some((insect) => {
          return insect.insect_group_id === selectedInsectGroupId;
        });
        if (location && isInGroup) {
          // Ensure lat and long are in the correct index.
          const coords = [];
          if (location.coordinates[0] >= -90 && location.coordinates[0] <= 90) {
            coords[0] = location.coordinates[0];
            coords[1] = location.coordinates[1];
          } else {
            coords[0] = location.coordinates[1];
            coords[1] = location.coordinates[0];
          }
          traps.push({
            id: trap.id,
            code: trap.code,
            name: trap.name,
            lureName: trap.trap_lure.name,
            displayColour: trap.trap_lure.display_color,
            locationDescription: trap.location_description,
            location: coords
          });
        }
      });
    });
    return traps;
  }, [traplogStore.trappingZones, traplogStore.trappingResults]);

  const fitBounds = (_map, { duration }) => {
    const map = _map || mapRef.current.getMap();
    const features = boundaryData.features;
    if (features && features.length > 0) {
      let minLng;
      let minLat;
      let maxLng;
      let maxLat;

      boundaryData.features.forEach((feature) => {
        const [newMinLng, newMinLat, newMaxLng, newMaxLat] = bbox(feature);
        if (minLng === undefined || newMinLng < minLng) minLng = newMinLng;
        if (maxLng === undefined || newMaxLng > maxLng) maxLng = newMaxLng;
        if (minLat === undefined || newMinLat < minLat) minLat = newMinLat;
        if (maxLat === undefined || newMaxLat > maxLat) maxLat = newMaxLat;
      });

      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        { padding: 50, duration }
      );
    }
  };

  const mapRefWrapper = useCallback((node) => {
    if (node) {
      if (boundaryData && !initialBoundsSet) {
        fitBounds(node, { duration: 0 });
        setInitialBoundsSet(true);
      }
    }
    mapRef.current = node;
  }, []);

  useEffect(() => {
    if (
      !initialBoundsSet &&
      boundaryData.features.length > 0 &&
      Number.isInteger(viewport.width)
    ) {
      setInitialBoundsSet(true);
    }
  }, [viewport, boundaryData, initialBoundsSet]);

  return [
    boundaryData,
    trapData,
    viewport,
    setViewport,
    mapRef,
    mapRefWrapper,
    initialBoundsSet,
    fitBounds
  ];
}
